import React, {
  useState,
  useEffect,
  useRef,
} from "react";
import { ConstraintConfigType } from "types/ConstraintsTypes";
import constraintsStore from "shared/store/constraintsStore";
import colorsStore from "shared/store/colorsStore";
import { parseValue } from "utils/misc";
import { GridItemWithRemove } from "./GridItemWithRemove";
import styles from "./content.module.css";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import constraintsProductMicrocatsStore from "shared/store/constraintsProductsMicrocatsStore";
import activityPanelStore from "shared/store/activityPanelStore";

type MinMaxWeights = {
  min: number;
  max: number;
  label?: string;
};

type MicroCategoryWeights = {
  microcategory_weights: Record<string, MinMaxWeights>;
};

type Props = {
  configData: ConstraintConfigType;
};

export function MicroCategoryContent({ configData }: Props) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  // Microcategory id → name map. The activity panel's container fetches this
  // once on mount (`fetchMicrocategoryNames`) so it's available both here and
  // in the Vincoli Invia recap. If the panel mounts before the fetch resolves
  // the inputs render "caricamento..." (see GridItemWithRemove label below).
  const microcategoryNames = activityPanelStore((s) => s.microcategoryNames);

  const {
    setMicrocategories,
    fetchSearchMicrocategories,
    microcategoriesSearchResults,
    resetMicrocategoriesSearchResults,
  } = constraintsProductMicrocatsStore();

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  // Read the API-scale weights map from the working document
  const micro = constraintsStore((s) => s.workingDocument?.microcategoria_advice);
  const apiWeights: Record<string, MinMaxWeights> =
    (micro as MicroCategoryWeights | undefined)?.microcategory_weights ?? {};

  // formValues: API scale (0–1) — derived from store, kept in sync via useEffect
  const [formValues, setFormValues] = useState<MicroCategoryWeights>({
    microcategory_weights: apiWeights,
  });

  // formLocalValues: display scale (0–100) — local state for responsive inputs
  const buildLocalFromApi = (
    weights: Record<string, MinMaxWeights>,
  ): MicroCategoryWeights => {
    const result: Record<string, { min: number; max: number }> = {};
    for (const mc in weights) {
      const w = weights[mc];
      result[mc] = {
        min: w.min * 100,
        max: w.max * 100,
      };
    }
    return { microcategory_weights: result };
  };

  const [formLocalValues, setFormLocalValues] = useState<MicroCategoryWeights>(
    buildLocalFromApi(apiWeights),
  );

  // Sync local state from store whenever the store's weights map changes
  // (e.g. agent update, profile apply, reset). We skip sync while the user is
  // actively editing an input (handled by the weight-change handler directly).
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (isEditingRef.current) return;
    const newFormValues: MicroCategoryWeights = {
      microcategory_weights: apiWeights,
    };
    setFormValues(newFormValues);
    setFormLocalValues(buildLocalFromApi(apiWeights));
  }, [apiWeights]);

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    const [id, field] = e.target.id.split("_");
    const config = {
      [e.target.id]: {
        type: "percentage",
        default: field === "min" ? 0 : 1,
        ge: 0,
        le: 1,
      },
    };

    const value = parseValue(e.target, config);

    const newApiWeights = {
      ...formValues.microcategory_weights,
      [id]: {
        ...formValues.microcategory_weights[id],
        [field]: value / 100,
      },
    };
    const newFormValues: MicroCategoryWeights = {
      microcategory_weights: newApiWeights,
    };

    setFormValues(newFormValues);
    setFormLocalValues((prev) => ({
      ...prev,
      microcategory_weights: {
        ...prev.microcategory_weights,
        [id]: {
          ...prev.microcategory_weights[id],
          [field]: value,
        },
      },
    }));

    constraintsStore
      .getState()
      .applyGroupEdits("microcategoria_advice", { microcategory_weights: newApiWeights });

    // Release editing guard after the current event loop tick so the store
    // sync useEffect does not clobber the local display value we just set.
    setTimeout(() => {
      isEditingRef.current = false;
    }, 0);
  };

  const handleRemoveCategory = (id: string) => {
    const newMicroCategoryWeights = { ...formValues.microcategory_weights };
    delete newMicroCategoryWeights[id];

    const newFormValues: MicroCategoryWeights = {
      ...formValues,
      microcategory_weights: newMicroCategoryWeights,
    };
    setFormValues(newFormValues);

    constraintsStore
      .getState()
      .applyGroupEdits("microcategoria_advice", {
        microcategory_weights: newMicroCategoryWeights,
      });
  };

  useEffect(() => {
    // Microcategory names are fetched eagerly by ActivityPanelContainer; we
    // only need to reset search results here.
    resetMicrocategoriesSearchResults();
  }, []);
  useEffect(() => {
    fetchSearchMicrocategories(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    setMicrocategories(formValues);
  }, [formValues]);

  //** prevent async results after delete */
  const [macrocatResult, setMacrocatResult] = useState<any>([]);
  useEffect(() => {
    if (searchTerm.length > 0) {
      setMacrocatResult(microcategoriesSearchResults);
    } else {
      setMacrocatResult([]);
    }
  }, [microcategoriesSearchResults, searchTerm]);

  const renderMicroCat = () => {
    return Object.entries(formValues.microcategory_weights).map(
      ([id, weights]: [string, MinMaxWeights]) => {
        let weightsLocal = formLocalValues.microcategory_weights[id];
        return (
          <div
            key={id}
            style={{ color: colorsConfig.finecoBlack, fontSize: 14 }}
          >
            <GridItemWithRemove
              cd={{
                [`${id}_min`]: {
                  type: "percentage",
                  label: microcategoryNames[id] || "caricamento...",
                  default: 0,
                  ge: 0,
                  le: 1,
                },
                [`${id}_max`]: {
                  type: "percentage",
                  default: 1,
                  ge: 0,
                  le: 1,
                },
              }}
              cv1={`${id}_min`}
              cv2={`${id}_max`}
              fv={{
                [`${id}_min`]:
                  typeof weightsLocal.min === "number"
                    ? weightsLocal.min.toFixed(2)
                    : weightsLocal.min,
                [`${id}_max`]:
                  typeof weightsLocal.max === "number"
                    ? weightsLocal.max.toFixed(2)
                    : weightsLocal.max,
              }}
              ohi={handleWeightChange}
              onRemove={() => handleRemoveCategory(id)}
              sectionKey="microcategoria_advice"
            />
          </div>
        );
      },
    );
  };
  return (
    <div style={{ marginTop: 5, height: "auto" }}>
      <div className={styles.searchContainer}>
        <Search size={16} className={styles.searchIcon} />
        <input
          type="text"
          placeholder={t("constraints.microcategories.searchFor")}
          className={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      {macrocatResult.length > -1 && (
        <div className={styles.dropdownContainer}>
          {macrocatResult.map((category) => {
            return (
              <div
                key={category.id_microcategoria_advice}
                className={styles.dropdownItem}
                onClick={() => {
                  const newApiWeights = {
                    [category.id_microcategoria_advice]: {
                      min: 0,
                      max: 1,
                    },
                    ...formValues.microcategory_weights,
                  };
                  setFormValues((prev) => ({
                    ...prev,
                    microcategory_weights: newApiWeights,
                  }));
                  setFormLocalValues((prev) => ({
                    ...prev,
                    microcategory_weights: {
                      [category.id_microcategoria_advice]: {
                        min: 0,
                        max: 100,
                      },
                      ...prev.microcategory_weights,
                    },
                  }));
                  setSearchTerm("");
                  resetMicrocategoriesSearchResults();
                  constraintsStore
                    .getState()
                    .applyGroupEdits("microcategoria_advice", {
                      microcategory_weights: newApiWeights,
                    });
                }}
              >
                {category.microcategoria_advice}
              </div>
            );
          })}
        </div>
      )}
      <div>
        <div className="space-y-4">{renderMicroCat()}</div>
      </div>
    </div>
  );
}
